import axios from "axios";

const ADMIN_API_BASE_URL = "/ofc/api/v1/admin/external-links";

export interface ExternalLink {
  id?: string;
  group: string;
  title: string;
  description?: string;
  link: string;
  displayOrder?: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

class ExternalLinksApiService {
  /**
   * Get all external links
   */
  async getAllExternalLinks(): Promise<ExternalLink[]> {
    try {
      const response =
        await axios.get<ApiResponse<ExternalLink[]>>(ADMIN_API_BASE_URL);
      if (!response.data.success || !response.data.data) {
        return [];
      }
      return response.data.data;
    } catch (error) {
      console.error("Error fetching external links:", error);
      throw error;
    }
  }

  /**
   * Get all active external links
   */
  async getAllActiveExternalLinks(): Promise<ExternalLink[]> {
    try {
      const links = await this.getAllExternalLinks();
      return links.filter((link) => link.isActive !== false);
    } catch (error) {
      console.error("Error fetching active external links:", error);
      throw error;
    }
  }

  /**
   * Get external links by group
   */
  async getExternalLinksByGroup(group: string): Promise<ExternalLink[]> {
    try {
      const response = await axios.get<ApiResponse<ExternalLink[]>>(
        `${ADMIN_API_BASE_URL}/group/${group}`,
      );
      if (!response.data.success || !response.data.data) {
        return [];
      }
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching external links for group ${group}:`, error);
      throw error;
    }
  }

  /**
   * Get a specific external link by ID
   */
  async getExternalLinkById(id: string): Promise<ExternalLink> {
    try {
      const response = await axios.get<ApiResponse<ExternalLink>>(
        `${ADMIN_API_BASE_URL}/${id}`,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error("Failed to fetch external link");
      }
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching external link ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new external link
   */
  async createExternalLink(link: ExternalLink): Promise<ExternalLink> {
    try {
      const response = await axios.post<ApiResponse<ExternalLink>>(
        ADMIN_API_BASE_URL,
        link,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error || "Failed to create external link",
        );
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to create external link",
        );
      }
      throw error;
    }
  }

  /**
   * Update an existing external link
   */
  async updateExternalLink(
    id: string,
    link: ExternalLink,
  ): Promise<ExternalLink> {
    try {
      const response = await axios.put<ApiResponse<ExternalLink>>(
        `${ADMIN_API_BASE_URL}/${id}`,
        link,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error || "Failed to update external link",
        );
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to update external link",
        );
      }
      throw error;
    }
  }

  /**
   * Delete an external link
   */
  async deleteExternalLink(id: string): Promise<void> {
    try {
      const response = await axios.delete<ApiResponse<void>>(
        `${ADMIN_API_BASE_URL}/${id}`,
      );
      if (!response.data.success) {
        throw new Error(
          response.data.error || "Failed to delete external link",
        );
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to delete external link",
        );
      }
      throw error;
    }
  }

  /**
   * Reorder external links within a group
   */
  async reorderExternalLinks(group: string, linkIds: string[]): Promise<void> {
    try {
      const response = await axios.post<ApiResponse<void>>(
        `${ADMIN_API_BASE_URL}/group/${group}/reorder`,
        linkIds,
      );
      if (!response.data.success) {
        throw new Error(
          response.data.error || "Failed to reorder external links",
        );
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to reorder external links",
        );
      }
      throw error;
    }
  }
}

export default new ExternalLinksApiService();
